import React, { useRef, useState, useEffect, useCallback } from 'react';
import { X, Type, PenTool, Upload, Check } from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';

const SIGNATURE_FONTS = [
  { id: 'dancing', name: 'Dancing Script', family: "'Dancing Script', cursive" },
  { id: 'great-vibes', name: 'Great Vibes', family: "'Great Vibes', cursive" },
  { id: 'pacifico', name: 'Pacifico', family: "'Pacifico', cursive" },
  { id: 'satisfy', name: 'Satisfy', family: "'Satisfy', cursive" },
  { id: 'caveat', name: 'Caveat', family: "'Caveat', cursive" },
  { id: 'sacramento', name: 'Sacramento', family: "'Sacramento', cursive" },
];

const FONT_LINK = 'https://fonts.googleapis.com/css2?family=Dancing+Script:wght@700&family=Great+Vibes&family=Pacifico&family=Satisfy&family=Caveat:wght@700&family=Sacramento&display=swap';

// Inject Google Fonts once
let fontsLoaded = false;
function loadSignatureFonts() {
  if (fontsLoaded) return;
  fontsLoaded = true;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = FONT_LINK;
  document.head.appendChild(link);
}

export default function SignatureModal({ isOpen, onClose, onConfirm, fieldLabel, fieldType = 'signature', currentValue, defaultName = '' }) {
  const [activeTab, setActiveTab] = useState('type');
  const [typedName, setTypedName] = useState('');
  const [selectedFont, setSelectedFont] = useState(SIGNATURE_FONTS[0].id);
  const [uploadedImage, setUploadedImage] = useState(null);
  const sigCanvasRef = useRef(null);
  const typeCanvasRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadSignatureFonts();
  }, []);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setTypedName(defaultName || '');
      setSelectedFont(SIGNATURE_FONTS[0].id);
      setUploadedImage(null);
      setActiveTab('type');
    }
  }, [isOpen]);

  // Render typed name to hidden canvas for export
  const renderTypedSignature = useCallback(() => {
    const canvas = typeCanvasRef.current;
    if (!canvas || !typedName.trim()) return null;

    const font = SIGNATURE_FONTS.find(f => f.id === selectedFont);
    const ctx = canvas.getContext('2d');

    // Step 1: Measure text at large size on a temp canvas
    const cw = 800;
    const ch = 300;
    canvas.width = cw;
    canvas.height = ch;

    let fontSize = fieldType === 'initials' ? 90 : 72;
    if (typedName.length > 12) fontSize = 60;
    if (typedName.length > 20) fontSize = 48;
    if (typedName.length > 30) fontSize = 36;

    ctx.clearRect(0, 0, cw, ch);
    ctx.font = `bold ${fontSize}px ${font.family}`;
    ctx.fillStyle = '#000000';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(typedName, cw / 2, ch / 2);

    // Step 2: Auto-crop to text bounds so signature fills the field
    const imageData = ctx.getImageData(0, 0, cw, ch);
    const { data } = imageData;
    let minX = cw, minY = ch, maxX = 0, maxY = 0;
    for (let y = 0; y < ch; y++) {
      for (let x = 0; x < cw; x++) {
        const alpha = data[(y * cw + x) * 4 + 3];
        if (alpha > 0) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }

    if (maxX <= minX || maxY <= minY) return canvas.toDataURL('image/png');

    // Add small padding
    const pad = 8;
    minX = Math.max(0, minX - pad);
    minY = Math.max(0, minY - pad);
    maxX = Math.min(cw - 1, maxX + pad);
    maxY = Math.min(ch - 1, maxY + pad);

    const cropW = maxX - minX + 1;
    const cropH = maxY - minY + 1;
    const cropped = ctx.getImageData(minX, minY, cropW, cropH);

    // Step 3: Output cropped canvas
    const outCanvas = document.createElement('canvas');
    outCanvas.width = cropW;
    outCanvas.height = cropH;
    const outCtx = outCanvas.getContext('2d');
    outCtx.putImageData(cropped, 0, 0);

    return outCanvas.toDataURL('image/png');
  }, [typedName, selectedFont, fieldType]);

  // Auto-crop whitespace from a canvas element, returns trimmed data URL
  const cropCanvas = (srcCanvas) => {
    const w = srcCanvas.width;
    const h = srcCanvas.height;
    const ctx = srcCanvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, w, h);
    const { data } = imageData;
    let minX = w, minY = h, maxX = 0, maxY = 0;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        if (data[(y * w + x) * 4 + 3] > 0) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX <= minX || maxY <= minY) return srcCanvas.toDataURL('image/png');
    const pad = 10;
    minX = Math.max(0, minX - pad);
    minY = Math.max(0, minY - pad);
    maxX = Math.min(w - 1, maxX + pad);
    maxY = Math.min(h - 1, maxY + pad);
    const cropW = maxX - minX + 1;
    const cropH = maxY - minY + 1;
    const out = document.createElement('canvas');
    out.width = cropW;
    out.height = cropH;
    out.getContext('2d').putImageData(ctx.getImageData(minX, minY, cropW, cropH), 0, 0);
    return out.toDataURL('image/png');
  };

  const handleConfirm = () => {
    let dataUrl = null;

    if (activeTab === 'type') {
      dataUrl = renderTypedSignature();
      if (!dataUrl || !typedName.trim()) return;
    } else if (activeTab === 'draw') {
      if (!sigCanvasRef.current || sigCanvasRef.current.isEmpty()) return;
      // Get the underlying canvas and crop whitespace
      const drawCanvas = sigCanvasRef.current.getCanvas();
      dataUrl = cropCanvas(drawCanvas);
    } else if (activeTab === 'upload') {
      if (!uploadedImage) return;
      dataUrl = uploadedImage;
    }

    if (dataUrl) {
      onConfirm(dataUrl);
      onClose();
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = (ev) => setUploadedImage(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = (ev) => setUploadedImage(ev.target.result);
    reader.readAsDataURL(file);
  };

  if (!isOpen) return null;

  const tabs = [
    { id: 'type', label: 'Type', icon: Type },
    { id: 'draw', label: 'Draw', icon: PenTool },
    { id: 'upload', label: 'Upload', icon: Upload },
  ];

  const currentFont = SIGNATURE_FONTS.find(f => f.id === selectedFont);
  const isTypeValid = activeTab === 'type' && typedName.trim().length > 0;
  const isDrawValid = activeTab === 'draw';
  const isUploadValid = activeTab === 'upload' && !!uploadedImage;
  const canConfirm = isTypeValid || isDrawValid || isUploadValid;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-[#1a1a2e] rounded-2xl w-full max-w-lg shadow-2xl border border-white/10 overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-5 py-3 sm:py-4 border-b border-white/10">
          <h3 className="text-white font-semibold text-base sm:text-lg">
            {fieldType === 'initials' ? 'Add Your Initials' : 'Add Your Signature'}
          </h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/10">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-1.5 sm:gap-2 py-2.5 sm:py-3 text-xs sm:text-sm font-medium transition-all
                  ${isActive
                    ? 'text-purple-400 border-b-2 border-purple-400 bg-purple-400/5'
                    : 'text-gray-400 hover:text-gray-300 hover:bg-white/5'
                  }`}
              >
                <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="p-4 sm:p-5 flex-1 overflow-y-auto">
          {/* TYPE TAB */}
          {activeTab === 'type' && (
            <div className="space-y-3 sm:space-y-4">
              <div>
                <label className="block text-xs sm:text-sm text-gray-400 mb-1.5">
                  {fieldType === 'initials' ? 'Type your initials' : 'Type your full name'}
                </label>
                <input
                  type="text"
                  value={typedName}
                  onChange={(e) => setTypedName(e.target.value)}
                  placeholder={fieldType === 'initials' ? 'e.g. J.D.' : 'e.g. John Doe'}
                  className="w-full bg-white/5 border border-white/15 rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 text-white placeholder-gray-500 focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-400/30 text-sm sm:text-base"
                  autoFocus
                  maxLength={fieldType === 'initials' ? 10 : 50}
                />
              </div>

              {/* Font Selection */}
              <div>
                <label className="block text-xs sm:text-sm text-gray-400 mb-1.5">Select style</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 sm:gap-2">
                  {SIGNATURE_FONTS.map(font => (
                    <button
                      key={font.id}
                      onClick={() => setSelectedFont(font.id)}
                      className={`px-2 sm:px-3 py-2 sm:py-2.5 rounded-xl border text-center transition-all truncate
                        ${selectedFont === font.id
                          ? 'border-purple-400 bg-purple-400/10 ring-1 ring-purple-400/30'
                          : 'border-white/10 hover:border-white/20 bg-white/5'
                        }`}
                      style={{
                        fontFamily: font.family,
                        fontSize: typedName ? (fieldType === 'initials' ? '18px' : '16px') : '14px',
                        color: selectedFont === font.id ? '#c084fc' : '#9ca3af',
                      }}
                    >
                      {typedName || font.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Preview */}
              {typedName && (
                <div>
                  <label className="block text-xs sm:text-sm text-gray-400 mb-1.5">Preview</label>
                  <div className="bg-white rounded-xl p-3 sm:p-4 flex items-center justify-center min-h-[60px] sm:min-h-[80px]">
                    <span
                      style={{
                        fontFamily: currentFont.family,
                        fontSize: fieldType === 'initials' ? '36px' : '32px',
                        color: '#000',
                        lineHeight: 1.2,
                      }}
                    >
                      {typedName}
                    </span>
                  </div>
                </div>
              )}

              {/* Hidden canvas for rendering */}
              <canvas ref={typeCanvasRef} style={{ display: 'none' }} width={800} height={300} />
            </div>
          )}

          {/* DRAW TAB */}
          {activeTab === 'draw' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-xs sm:text-sm text-gray-400">
                  {fieldType === 'initials' ? 'Draw your initials below' : 'Draw your signature below'}
                </label>
                <button
                  onClick={() => sigCanvasRef.current?.clear()}
                  className="text-xs text-red-400 hover:text-red-300 px-2 py-1 rounded-lg hover:bg-red-400/10 transition-colors"
                >
                  Clear
                </button>
              </div>
              <div className="bg-white rounded-xl overflow-hidden border-2 border-dashed border-white/20 relative">
                <SignatureCanvas
                  ref={sigCanvasRef}
                  penColor="#000000"
                  minWidth={2}
                  maxWidth={4}
                  velocityFilterWeight={0.7}
                  canvasProps={{
                    className: 'w-full',
                    style: {
                      width: '100%',
                      height: '200px',
                      touchAction: 'none',
                    },
                  }}
                  backgroundColor="rgba(255,255,255,1)"
                />
                {/* Baseline guide */}
                <div className="absolute bottom-10 left-4 right-4 border-b border-gray-200 pointer-events-none" />
              </div>
              <p className="text-[10px] sm:text-xs text-gray-500 text-center">
                Use your mouse or finger to sign
              </p>
            </div>
          )}

          {/* UPLOAD TAB */}
          {activeTab === 'upload' && (
            <div className="space-y-3">
              <label className="text-xs sm:text-sm text-gray-400 block">
                Upload an image of your {fieldType === 'initials' ? 'initials' : 'signature'}
              </label>
              {uploadedImage ? (
                <div className="space-y-2">
                  <div className="bg-white rounded-xl p-3 sm:p-4 flex items-center justify-center min-h-[100px] sm:min-h-[120px] relative">
                    <img
                      src={uploadedImage}
                      alt="Uploaded signature"
                      className="max-w-full max-h-[100px] sm:max-h-[120px] object-contain"
                    />
                    <button
                      onClick={() => { setUploadedImage(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  onDrop={handleDrop}
                  onDragOver={(e) => e.preventDefault()}
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-white/15 rounded-xl p-6 sm:p-8 flex flex-col items-center justify-center cursor-pointer hover:border-purple-400/40 hover:bg-purple-400/5 transition-all min-h-[120px] sm:min-h-[140px]"
                >
                  <Upload className="w-7 h-7 sm:w-8 sm:h-8 text-gray-500 mb-2" />
                  <p className="text-xs sm:text-sm text-gray-400 text-center">
                    Click or drag & drop
                  </p>
                  <p className="text-[10px] sm:text-xs text-gray-500 mt-1">
                    PNG, JPG, or SVG
                  </p>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                onChange={handleFileUpload}
                className="hidden"
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 sm:gap-3 px-4 sm:px-5 py-3 sm:py-4 border-t border-white/10 bg-white/[0.02]">
          <button
            onClick={onClose}
            className="px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm text-gray-400 hover:text-white rounded-xl hover:bg-white/5 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!canConfirm}
            className={`px-4 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-medium rounded-xl flex items-center gap-1.5 transition-all
              ${canConfirm
                ? 'bg-purple-500 hover:bg-purple-600 text-white shadow-lg shadow-purple-500/20'
                : 'bg-white/5 text-gray-500 cursor-not-allowed'
              }`}
          >
            <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            Apply {fieldType === 'initials' ? 'Initials' : 'Signature'}
          </button>
        </div>
      </div>
    </div>
  );
}
